import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EnhancedDynamicTablePagination from "../../components/common/CommonTable/EnhancedDynamicTablePagination";
import CommonButton from "../../components/common11/Buttons/CommonButton";
import CommonCheckbox from "../../components/common11/formInputs/CommonCheckbox";
import CommonDatePicker from "../../components/common11/formInputs/CommonDatePicker";
import SearchDropdown from "../../components/common11/formInputs/SearchDropdown";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../components/common11/toastUtils";
import {
  approvePurchaseOrderItems,
  clearSelectedPurchaseOrder,
  fetchPurchaseLookups,
  fetchPurchaseOrderDetail,
  fetchPurchaseOrders,
  getDefaultFilters,
  savePurchaseOrderItems,
  searchPurchaseSuppliers,
  setPurchaseFilters,
} from "../../store/slices/purchaseOrderSlice";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const calcLineAmounts = (item) => {
  const qty = Number(item.qty) || 0;
  const rate = Number(item.rate) || 0;
  const gstPer = Number(item.gstPer) || 0;
  const discPer = Number(item.discPer) || 0;
  const gross = qty * rate;
  const discAmt = round2((gross * discPer) / 100);
  const taxable = gross - discAmt;
  const gstAmt = round2((taxable * gstPer) / 100);
  const totalAmt = round2(gross);
  return {
    ...item,
    qty,
    totalAmt,
    discAmt,
    gstAmt,
  };
};

function PurchaseOrderApproval() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const {
    filters,
    stores,
    suppliers,
    purchaseOrders,
    selectedPo,
    loading,
    detailLoading,
    saving,
    approving,
  } = useSelector((state) => state.purchaseOrder);

  const defaults = getDefaultFilters();
  const [draftItems, setDraftItems] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      toStore: filters?.toStore ?? defaults.toStore,
      supplier: filters?.supplier ?? defaults.supplier,
      fromDate: filters?.fromDate ?? defaults.fromDate,
      toDate: filters?.toDate ?? defaults.toDate,
      cancelledPo: Boolean(filters?.cancelledPo),
    },
  });

  useEffect(() => {
    (async () => {
      const lookups = await dispatch(fetchPurchaseLookups());
      if (fetchPurchaseLookups.rejected.match(lookups)) {
        showErrorToast(lookups.payload || "Failed to load stores/suppliers");
      } else if (!(lookups.payload?.stores || []).length) {
        showInfoToast("No active stores found. Check mst_StoreMaster.", {
          duration: 2500,
        });
      }

      const list = await dispatch(fetchPurchaseOrders(filters || defaults));
      if (fetchPurchaseOrders.rejected.match(list)) {
        showErrorToast(list.payload || "Failed to load purchase orders");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load
  }, [dispatch]);

  const handleSupplierSearch = useCallback(
    (term) => dispatch(searchPurchaseSuppliers(term || "")),
    [dispatch]
  );

  const poTableData = useMemo(
    () =>
      (purchaseOrders || []).map((po) => ({
        poId: po.poId,
        purNo: po.purNo,
        date: po.date,
        supplierName: po.supplierName,
        totalAmt: po.totalAmt,
        discAmt: po.discAmt,
        gstAmt: po.gstAmt,
        netAmt: po.netAmt,
        verify: po.verify,
      })),
    [purchaseOrders]
  );

  const handleSearch = async (data) => {
    const next = {
      toStore: data.toStore || null,
      supplier: data.supplier || null,
      fromDate: data.fromDate,
      toDate: data.toDate,
      cancelledPo: Boolean(data.cancelledPo),
    };
    dispatch(setPurchaseFilters(next));
    setPage(0);
    dispatch(clearSelectedPurchaseOrder());
    setDraftItems([]);
    const result = await dispatch(fetchPurchaseOrders(next));
    if (fetchPurchaseOrders.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load purchase orders");
      return;
    }
    showInfoToast("Purchase order list filtered.", { duration: 1500 });
  };

  const handleResetFilters = async () => {
    const next = getDefaultFilters();
    dispatch(setPurchaseFilters(next));
    setPage(0);
    dispatch(clearSelectedPurchaseOrder());
    setDraftItems([]);
    reset({
      toStore: null,
      supplier: null,
      fromDate: next.fromDate,
      toDate: next.toDate,
      cancelledPo: false,
    });
    const result = await dispatch(fetchPurchaseOrders(next));
    if (fetchPurchaseOrders.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load purchase orders");
    }
  };

  const handlePoRowClick = async (row) => {
    const result = await dispatch(fetchPurchaseOrderDetail(row.poId));
    if (fetchPurchaseOrderDetail.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load PO items");
      setDraftItems([]);
      return;
    }
    setDraftItems(
      (result.payload?.items || []).map((item) => calcLineAmounts({ ...item }))
    );
    setItemsPage(0);
  };

  const handleQtyChange = (purDetId, rawValue) => {
    const qty = Math.max(0, Number(rawValue) || 0);
    setDraftItems((prev) =>
      prev.map((item) =>
        item.purDetId === purDetId ? calcLineAmounts({ ...item, qty }) : item
      )
    );
  };

  const handleSave = async () => {
    if (!selectedPo?.poId) return;
    const result = await dispatch(
      savePurchaseOrderItems({
        purchaseId: selectedPo.poId,
        items: draftItems,
        userId: authUser?.userId || 1,
      })
    );
    if (savePurchaseOrderItems.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to save");
      return;
    }
    setDraftItems(
      (result.payload?.items || []).map((item) => calcLineAmounts({ ...item }))
    );
    showSuccessToast(`PO ${selectedPo.purNo || selectedPo.poId} saved.`);
    dispatch(fetchPurchaseOrders(filters || defaults));
  };

  const handleApprove = async () => {
    if (!selectedPo?.poId) return;
    const purNo = selectedPo.purNo || selectedPo.poId;
    const result = await dispatch(
      approvePurchaseOrderItems({
        purchaseId: selectedPo.poId,
        items: draftItems,
        userId: authUser?.userId || 1,
      })
    );
    if (approvePurchaseOrderItems.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to approve");
      return;
    }
    setDraftItems(
      (result.payload?.items || []).map((item) => calcLineAmounts({ ...item }))
    );
    showSuccessToast(`PO ${purNo} approved.`);
    dispatch(fetchPurchaseOrders(filters || defaults));
  };

  const poRenderCustomColumns = {
    purNo: (row) => row.purNo || "—",
    date: (row) => row.date || "—",
    supplierName: (row) => row.supplierName || "—",
    totalAmt: (row) => Number(row.totalAmt || 0).toFixed(2),
    discAmt: (row) => Number(row.discAmt || 0).toFixed(2),
    gstAmt: (row) => Number(row.gstAmt || 0).toFixed(2),
    netAmt: (row) => Number(row.netAmt || 0).toFixed(2),
    verify: (row) => (
      <span
        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${
          row.verify === "Close"
            ? "bg-green-100 text-green-800 border-green-300"
            : "bg-amber-100 text-amber-800 border-amber-300"
        }`}
      >
        {row.verify || "Pending"}
      </span>
    ),
  };

  const isApproved = selectedPo?.verify === "Close";

  const itemsRenderCustomColumns = {
    itemName: (row) => row.itemName || "—",
    qty: (row) => (
      <input
        type="number"
        min={0}
        step={1}
        value={row.qty}
        onChange={(e) => handleQtyChange(row.purDetId, e.target.value)}
        disabled={isApproved}
        className="w-20 rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#217E90] disabled:bg-gray-100"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    rate: (row) => Number(row.rate || 0).toFixed(2),
    totalAmt: (row) => Number(row.totalAmt || 0).toFixed(2),
    gstPer: (row) => Number(row.gstPer || 0).toFixed(2),
    gstAmt: (row) => Number(row.gstAmt || 0).toFixed(2),
    discPer: (row) => Number(row.discPer || 0).toFixed(2),
    discAmt: (row) => Number(row.discAmt || 0).toFixed(2),
  };

  const hasSelection = Boolean(selectedPo?.poId);

  return (
    <div className="p-2 sm:p-3 h-[calc(100vh-3rem)] max-h-[calc(100vh-3rem)] flex flex-col overflow-hidden pb-16">
      <div className="bg-white rounded-lg shadow-sm border flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="sticky top-0 z-10 bg-white rounded-t-lg px-2 py-1 sm:px-3 sm:py-1.5 border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm sm:text-base font-semibold text-[#217E90] flex items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-0.5 rounded hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#217E90]"
                aria-label="Go back"
              >
                <ArrowUturnLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              </button>
              <span className="truncate">Purchase Order Approval</span>
            </h2>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden bg-[#217E90] text-white px-3 py-2 rounded-lg text-sm font-medium"
            >
              {showFilters ? "Hide" : "Show"} Filters
            </button>
          </div>
        </div>

        <div className="p-2 sm:p-3 flex-1 min-h-0 flex flex-col space-y-3 overflow-hidden">
          <form onSubmit={handleSubmit(handleSearch)} className="flex-shrink-0">
            <div
              className={`${
                showFilters ? "block" : "hidden"
              } lg:block rounded-lg border border-gray-200`}
            >
              <div className="p-3 sm:p-4">
                <div className="flex flex-wrap items-end gap-2 sm:gap-3 w-full">
                  <div className="w-full sm:w-[11rem] lg:w-[12rem] shrink-0 min-w-0">
                    <SearchDropdown
                      name="toStore"
                      label="To Store"
                      control={control}
                      dataArray={stores}
                      isSearchable
                      isClearable
                      selectSize="h-9"
                      fontSize="text-xs"
                    />
                  </div>
                  <div className="w-full sm:w-[12rem] lg:w-[13rem] shrink-0 min-w-0">
                    <SearchDropdown
                      name="supplier"
                      label="Supplier"
                      control={control}
                      dataArray={suppliers}
                      onSearch={handleSupplierSearch}
                      isSearchable
                      isClearable
                      selectSize="h-9"
                      fontSize="text-xs"
                    />
                  </div>
                  <div className="w-full sm:w-[9.5rem] lg:w-[8.75rem] shrink-0">
                    <CommonDatePicker
                      name="fromDate"
                      label="From Date"
                      control={control}
                      error={errors.fromDate}
                      maxDate={watch("toDate")}
                      fontSize="text-xs"
                    />
                  </div>
                  <div className="w-full sm:w-[9.5rem] lg:w-[8.75rem] shrink-0">
                    <CommonDatePicker
                      name="toDate"
                      label="To Date"
                      control={control}
                      error={errors.toDate}
                      minDate={watch("fromDate")}
                      fontSize="text-xs"
                    />
                  </div>
                  <div className="shrink-0 pb-1">
                    <CommonCheckbox
                      name="cancelledPo"
                      control={control}
                      label="Cancelled PO"
                      fontSize="text-xs"
                      checkboxSize="h-4 w-4"
                      checkboxColor="bg-[#217E90]"
                    />
                  </div>
                  <div className="hidden lg:block flex-1 min-w-2" aria-hidden="true" />
                  <CommonButton
                    label={loading ? "Loading..." : "Search"}
                    type="submit"
                    className="bg-[#217E90] hover:bg-[#1a5e70] text-white w-full sm:w-auto shrink-0 px-4"
                    size="sm"
                    disabled={loading}
                  />
                  <CommonButton
                    label="Reset"
                    type="button"
                    onClick={handleResetFilters}
                    variant="danger-outline"
                    size="sm"
                    className="w-full sm:w-auto shrink-0 px-3"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </form>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0">
            <div className="min-h-0 flex flex-col overflow-hidden rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200 bg-slate-50">
                <h3 className="text-xs sm:text-sm font-semibold text-[#217E90]">
                  Purchase Order List
                </h3>
              </div>
              <div className="hidden lg:flex flex-1 min-h-0 flex-col overflow-hidden [&>div]:h-full [&>div]:min-h-0 [&>div]:flex [&>div]:flex-col [&>div]:border-0 [&>div]:rounded-none [&>div]:shadow-none [&_.overflow-auto]:!max-h-none [&_.overflow-auto]:flex-1">
                <EnhancedDynamicTablePagination
                  dataResult={poTableData}
                  setDataResult={() => {}}
                  fetchData={() => {}}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  count={poTableData.length}
                  removeHeaders={["poId"]}
                  columnsAfterActions={["verify"]}
                  renderCustomColumns={poRenderCustomColumns}
                  columnHeaderLabels={{
                    purNo: "Pur. No",
                    date: "Date",
                    supplierName: "Supplier Name",
                    totalAmt: "Total Amt",
                    discAmt: "Disc Amt",
                    gstAmt: "GST Amt",
                    netAmt: "Net Amt",
                    verify: "Verify",
                  }}
                  highlightRow
                  customRowBgColor="#F0F8FF"
                  onRowClick={handlePoRowClick}
                  tableClass="min-h-0"
                  rowHoverEffect="highlight"
                />
              </div>
              <div className="block lg:hidden flex-1 min-h-0 overflow-y-auto space-y-2 p-2">
                {loading ? (
                  <p className="text-center text-gray-500 py-8 text-sm">Loading...</p>
                ) : poTableData.length === 0 ? (
                  <p className="text-center text-gray-500 py-8 text-sm">
                    No purchase orders found
                  </p>
                ) : (
                  poTableData.map((row) => (
                    <button
                      key={row.poId}
                      type="button"
                      onClick={() => handlePoRowClick(row)}
                      className={`w-full text-left border rounded-lg p-3 ${
                        selectedPo?.poId === row.poId
                          ? "border-[#217E90] bg-[#F0F8FF]"
                          : "border-gray-200 hover:bg-[#F0F8FF]"
                      }`}
                    >
                      <p className="font-semibold text-sm text-[#217E90]">
                        PO {row.purNo}
                      </p>
                      <p className="text-xs text-gray-500">
                        {row.supplierName} · {row.date} · Net{" "}
                        {Number(row.netAmt || 0).toFixed(2)}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="min-h-0 flex flex-col overflow-hidden rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200 bg-slate-50">
                <h3 className="text-xs sm:text-sm font-semibold text-[#217E90]">
                  PO Items
                  {selectedPo ? (
                    <span className="ml-2 font-normal text-gray-500">
                      (PO {selectedPo.purNo})
                    </span>
                  ) : null}
                  {detailLoading ? (
                    <span className="ml-2 font-normal text-gray-400">Loading...</span>
                  ) : null}
                </h3>
              </div>
              {!hasSelection ? (
                <div className="flex-1 flex items-center justify-center p-6">
                  <p className="text-sm text-gray-500 text-center">
                    Select a purchase order
                  </p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden [&>div]:h-full [&>div]:min-h-0 [&>div]:flex [&>div]:flex-col [&>div]:border-0 [&>div]:rounded-none [&>div]:shadow-none [&_.overflow-auto]:!max-h-none [&_.overflow-auto]:flex-1">
                  <EnhancedDynamicTablePagination
                    dataResult={draftItems}
                    setDataResult={() => {}}
                    fetchData={() => {}}
                    page={itemsPage}
                    setPage={setItemsPage}
                    rowsPerPage={itemsRowsPerPage}
                    setRowsPerPage={setItemsRowsPerPage}
                    count={draftItems.length}
                    removeHeaders={["itemId", "purDetId"]}
                    renderCustomColumns={itemsRenderCustomColumns}
                    columnHeaderLabels={{
                      itemName: "Item Name",
                      qty: "Qty",
                      rate: "Rate",
                      totalAmt: "Total Amt",
                      gstPer: "GST Per",
                      gstAmt: "GST Amt",
                      discPer: "Disc Per",
                      discAmt: "Disc Amt",
                    }}
                    highlightRow={false}
                    tableClass="min-h-0"
                    rowHoverEffect="none"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-4 right-4 z-50 flex gap-2 sm:gap-3 pr-4 sm:pr-6">
        <CommonButton
          label={saving ? "Saving..." : "Save"}
          type="button"
          onClick={handleSave}
          disabled={!hasSelection || isApproved || saving || approving}
          variant="primary-filled"
          size="sm"
          className="flex-none px-3 md:px-5 text-xs md:text-sm bg-[#217E90] hover:bg-[#1a6575]"
        />
        <CommonButton
          label={approving ? "Approving..." : "Approve"}
          type="button"
          onClick={handleApprove}
          disabled={!hasSelection || isApproved || saving || approving}
          variant="success-filled"
          size="sm"
          className="flex-none px-3 md:px-5 text-xs md:text-sm"
        />
      </div>
    </div>
  );
}

export default PurchaseOrderApproval;
